import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Clock, Users, FileText, ReceiptEuro, ArrowUpRight } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { getDashboard } from "@/lib/api";
import { fmtEur, fmtDate } from "@/lib/format";
import { useI18n } from "@/i18n";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";

const Stat = ({ icon: Icon, label, value, accent, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: i * 0.06, duration: 0.4 }}
  >
    <Card className="border-border p-6 transition-transform duration-200 hover:-translate-y-1 hover:shadow-sm" data-testid={`stat-${label}`}>
      <div className="flex items-start justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent}`}>
          <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
        </div>
      </div>
      <p className="font-display mt-4 text-3xl font-bold tracking-tight text-slate-900 font-mono-num">{value}</p>
    </Card>
  </motion.div>
);

const Dashboard = () => {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getDashboard().then(setData).catch(() => {});
  }, []);

  if (!data) return <div className="text-muted-foreground">{t("common.loading")}</div>;

  const monthLabels = data.monthly.map((m) => {
    const [y, mo] = m.month.split("-");
    return { ...m, label: `${mo}.${y.slice(2)}` };
  });
  const hasRevenue = data.monthly.some((m) => m.revenue > 0);

  return (
    <div>
      <PageHeader title={t("dashboard.title")} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat i={0} icon={TrendingUp} label={t("dashboard.revenue")} value={fmtEur(data.revenue)} accent="bg-emerald-50 text-emerald-600" />
        <Stat i={1} icon={Clock} label={t("dashboard.outstanding")} value={fmtEur(data.outstanding)} accent="bg-amber-50 text-amber-600" />
        <Stat i={2} icon={ReceiptEuro} label={t("dashboard.invoices")} value={data.invoice_count} accent="bg-accent text-primary" />
        <Stat i={3} icon={Users} label={t("dashboard.customers")} value={data.customer_count} accent="bg-slate-100 text-slate-600" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <h2 className="font-display text-lg font-semibold text-slate-900">{t("dashboard.monthlyRevenue")}</h2>
          <div className="mt-6 h-72">
            {hasRevenue ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthLabels} margin={{ left: -10, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} stroke="#94a3b8" />
                  <YAxis tickLine={false} axisLine={false} fontSize={12} stroke="#94a3b8" tickFormatter={(v) => `${v}€`} />
                  <Tooltip formatter={(v) => fmtEur(v)} cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="revenue" fill="hsl(223 100% 33%)" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">{t("dashboard.noData")}</div>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="font-display text-lg font-semibold text-slate-900">{t("dashboard.recentInvoices")}</h2>
          <div className="mt-4 divide-y divide-border">
            {data.recent_invoices.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">{t("dashboard.noData")}</p>
            )}
            {data.recent_invoices.map((inv) => (
              <button
                key={inv.id}
                data-testid={`recent-invoice-${inv.id}`}
                onClick={() => navigate(`/invoices/${inv.id}`)}
                className="group flex w-full items-center justify-between py-3 text-left"
              >
                <div>
                  <p className="text-sm font-semibold text-slate-800">{inv.number}</p>
                  <p className="text-xs text-muted-foreground">{inv.customer?.name} · {fmtDate(inv.issue_date)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono-num text-sm font-semibold text-slate-800">{fmtEur(inv.total)}</span>
                  <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-primary" />
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
